<template>
  <div>
    <PageDescriptionEditor page-title="Events"
      default-description="Join us for author readings, book launches, and literary events throughout the year." />

    <div class="mb-6">
      <h2 class="font-header text-[3rem] font-bold text-primary">Events Management</h2>
    </div>

    <div v-if="showForm" class="bg-white p-6 rounded-lg shadow-md mb-6">
      <h3 class="font-header text-[1.5rem] font-bold mb-4">{{ editingEvent ? 'Edit' : 'Add' }} Event</h3>
      <form @submit.prevent="saveEvent" class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Title</label>
            <input v-model="formData.title" type="text" required
              class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
          </div>
          <div>
            <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Date & Time</label>
            <input v-model="formData.date" type="datetime-local" required
              class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
          </div>
          <div>
            <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Location</label>
            <input v-model="formData.location" type="text"
              class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
          </div>
          <div>
            <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Ticket Price</label>
            <input v-model.number="formData.ticket_price" type="number" step="0.01"
              class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
          </div>
          <div>
            <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Max Attendees</label>
            <input v-model.number="formData.max_attendees" type="number" min="1"
              class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
          </div>
          <div class="col-span-2">
            <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Image</label>
            <div class="space-y-2">
              <div class="flex gap-2">
                <input ref="fileInput" type="file" accept="image/*,.heic,.heif" @change="handleFileSelect"
                  class="hidden" />
                <button type="button" @click="() => fileInput?.click()"
                  class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-body text-[1.125rem] leading-[1.75]">
                  Upload Image
                </button>
                <input v-model="formData.image" type="url" placeholder="Or enter image URL"
                  class="flex-1 px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
              </div>
              <div v-if="formData.image && !uploading" class="mt-2 space-y-2">
                <div class="flex flex-wrap items-center gap-2">
                  <button type="button" @click="loadImageForCropping" :disabled="showCropModal || loadingCropImage"
                    :class="['px-4 py-2 rounded-md transition-colors font-body text-[1.125rem] leading-[1.75]', (showCropModal || loadingCropImage) ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800']">
                    {{ loadingCropImage ? 'Loading...' : 'Crop Image' }}
                  </button>
                  <button type="button" @click="clearImage"
                    class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-body text-[1.125rem] leading-[1.75]">
                    Remove Image
                  </button>
                </div>
                <div class="w-full max-w-full overflow-hidden">
                  <div :style="getImageStyle()" class="border border-creme-300 overflow-hidden mx-auto">
                    <img :src="formData.image" alt="Preview" class="w-full h-full object-cover" />
                  </div>
                </div>
                <div
                  v-if="formData.image_crop_dimensions && (formData.image_crop_dimensions.width > 0 || formData.image_crop_dimensions.height > 0)"
                  class="space-y-2">
                  <div class="font-body text-body text-gray-600">
                    Dimensions: {{ Math.round(formData.image_crop_dimensions.width) }} × {{
                      Math.round(formData.image_crop_dimensions.height) }}px
                  </div>
                  <div class="grid grid-cols-2 gap-2">
                    <div>
                      <label class="block font-body text-body mb-1">Width (px)</label>
                      <input v-model.number="formData.image_crop_dimensions.width" type="number" step="0.1" min="0"
                        @input="updatePreviewDimensions"
                        class="w-full px-2 py-1 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                    </div>
                    <div>
                      <label class="block font-body text-body mb-1">Height (px)</label>
                      <input v-model.number="formData.image_crop_dimensions.height" type="number" step="0.1" min="0"
                        @input="updatePreviewDimensions"
                        class="w-full px-2 py-1 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                    </div>
                  </div>
                  <div class="p-3 bg-creme-100 rounded-md border border-creme-300">
                    <p class="font-header text-[1.5rem] font-bold text-primary mb-2">True-to-Scale Preview:</p>
                    <div class="flex items-center justify-center">
                      <div :style="getTrueToScalePreviewStyle()"
                        class="border-2 border-gray-400 bg-white overflow-hidden shadow-sm">
                        <img :src="imagePreview || formData.image" alt="True scale preview"
                          class="w-full h-full object-cover" />
                      </div>
                    </div>
                    <p class="font-body text-body text-gray-500 mt-2 text-center">This is how the image will appear at
                      actual size</p>
                  </div>
                </div>
              </div>
              <div v-if="uploading" class="font-body text-body text-gray-600">Uploading...</div>
            </div>
          </div>
          <div class="col-span-2">
            <label class="block font-body text-body mb-1">Description</label>
            <textarea v-model="formData.description" rows="4" required
              class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body"></textarea>
          </div>
        </div>
        <div class="flex gap-2">
          <button type="submit"
            class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-body">
            {{ editingEvent ? 'Update' : 'Create' }}
          </button>
          <button type="button" @click="cancelForm"
            class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-body text-body">
            Cancel
          </button>
        </div>
      </form>
    </div>

    <div class="mb-4 flex justify-end">
      <button @click="showForm = true; editingEvent = null"
        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
        Add New Event
      </button>
    </div>

    <div class="bg-white rounded-lg shadow-md overflow-hidden">
      <div class="w-full overflow-x-auto">
        <table class="w-full min-w-max">
          <thead class="bg-green-700 text-white">
            <tr>
              <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Title</th>
              <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Date</th>
              <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Location</th>
              <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Attendees</th>
              <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Status</th>
              <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="event in events" :key="event.id" class="border-b border-creme-200 hover:bg-creme-50">
              <td class="px-4 py-3 font-marketing text-gray-900" data-no-translate>{{ event.title }}</td>
              <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap">{{ formatDateTime(event.date) }}</td>
              <td class="px-4 py-3 font-marketing text-gray-900">{{ event.location || 'TBD' }}</td>
              <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap">{{ event.current_attendees }}{{
                event.max_attendees ? ` /
                ${event.max_attendees}` : '' }}</td>
              <td class="px-4 py-3 font-marketing whitespace-nowrap">
                <span :class="event.is_active ? 'text-green-600' : 'text-red-600'">
                  {{ event.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <button @click="editEvent(event)"
                  class="text-green-700 hover:text-green-800 mr-3 font-marketing">Edit</button>
                <button @click="deleteEvent(event.id)"
                  class="text-red-600 hover:text-red-700 font-marketing">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="loading" class="p-4 text-center">
        <Loader size="medium" />
      </div>
      <div v-else-if="events.length === 0" class="p-8 text-center text-gray-500 font-marketing">
        No events found
      </div>
      <div v-if="pagination && pagination.total_pages > 1"
        class="p-4 flex justify-between items-center border-t border-creme-200">
        <button @click="loadEvents(currentPage - 1)" :disabled="!pagination.prev_page"
          :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
          Previous
        </button>
        <span class="font-marketing text-gray-700">
          Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
        </span>
        <button @click="loadEvents(currentPage + 1)" :disabled="!pagination.next_page"
          :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
          Next
        </button>
      </div>
    </div>

    <div class="mt-12">
      <h2 class="text-3xl font-marketing font-bold text-primary mb-6">Ticket Management</h2>

      <div class="bg-white p-6 rounded-lg shadow-md mb-6">
        <div class="mb-4">
          <label class="block text-sm font-marketing mb-1">Search by Ticket Code or Transaction ID</label>
          <div class="flex items-center border-b-2 border-gray-300 pb-0">
            <input v-model="transactionSearch" type="text" placeholder="Enter ticket code or transaction ID"
              @keydown.enter="searchTickets"
              class="flex-1 bg-transparent text-primary placeholder-gray-400 border-none outline-none text-base font-sans mr-4" />
            <div class="flex items-center">
              <button v-if="transactionSearch.trim()" @click="clearTicketSearch"
                class="p-2 text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0" aria-label="Clear search">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <button @click="searchTickets"
                class="p-2 bg-green-600 text-white hover:bg-green-700 transition-colors flex-shrink-0"
                aria-label="Search">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="transactionSearch">
        <div v-if="ticketsLoading" class="bg-white p-8 rounded-lg shadow-md text-center text-gray-500 font-marketing">
          Loading tickets...
        </div>
        <div v-else-if="tickets.length === 0"
          class="bg-white p-8 rounded-lg shadow-md text-center text-gray-500 font-marketing">
          No tickets found for this search term
        </div>
        <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
          <div class="p-4 bg-green-50 border-b border-green-200 flex justify-between items-center">
            <div>
              <p class="font-marketing font-semibold text-primary">
                Search: {{ transactionSearch }}
              </p>
              <p class="text-sm text-gray-600 font-sans">
                {{ tickets.length }} ticket(s) found
              </p>
            </div>
            <button v-if="hasUnusedTickets" @click="markAllTicketsUsed"
              class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
              Mark All as Used
            </button>
          </div>
          <div class="w-full overflow-x-auto">
            <table class="w-full min-w-max">
              <thead class="bg-green-700 text-white">
                <tr>
                  <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Ticket Code</th>
                  <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Event</th>
                  <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Date</th>
                  <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Ticket Holder</th>
                  <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Status</th>
                  <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ticket in tickets" :key="ticket.id" class="border-b border-creme-200 hover:bg-creme-50">
                  <td class="px-4 py-3 font-mono font-semibold text-gray-900 whitespace-nowrap">{{ ticket.ticket_code }}
                  </td>
                  <td class="px-4 py-3 font-marketing text-gray-900">{{ ticket.event_title }}</td>
                  <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap">{{
                    formatDateTime(ticket.event_date) }}</td>
                  <td class="px-4 py-3 font-marketing text-gray-900">
                    {{ ticket.ticket_holder_name || 'Guest' }}
                  </td>
                  <td class="px-4 py-3 font-marketing whitespace-nowrap">
                    <span :class="ticket.is_used ? 'text-green-600' : 'text-orange-600'">
                      {{ ticket.is_used ? 'Used' : 'Unused' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <button v-if="!ticket.is_used" @click="markTicketUsed(ticket.id)"
                      class="px-4 py-1 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing text-sm">
                      Mark as Used
                    </button>
                    <span v-else class="text-gray-400 font-marketing text-sm">Already used</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-else>
        <div class="flex gap-2 mb-4 border-b border-gray-300">
          <button @click="activeTicketTab = 'valid'; loadValidTickets(1)"
            :class="['px-6 py-3 font-marketing font-semibold transition-colors', activeTicketTab === 'valid' ? 'bg-green-700 text-white border-b-2 border-green-700' : 'bg-white text-gray-700 hover:bg-gray-50']">
            Valid Tickets
          </button>
          <button @click="activeTicketTab = 'used'; loadUsedTickets(1)"
            :class="['px-6 py-3 font-marketing font-semibold transition-colors', activeTicketTab === 'used' ? 'bg-green-700 text-white border-b-2 border-green-700' : 'bg-white text-gray-700 hover:bg-gray-50']">
            Used Tickets
          </button>
        </div>

        <div v-if="activeTicketTab === 'valid'">
          <div v-if="validTicketsLoading"
            class="bg-white p-8 rounded-lg shadow-md text-center text-gray-500 font-marketing">
            Loading valid tickets...
          </div>
          <div v-else-if="validTickets.length === 0"
            class="bg-white p-8 rounded-lg shadow-md text-center text-gray-500 font-marketing">
            No valid tickets found
          </div>
          <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="p-4 bg-green-50 border-b border-green-200">
              <p class="font-marketing font-semibold text-primary">
                Valid Tickets ({{ validTicketsPagination?.total || 0 }} total)
              </p>
            </div>
            <div class="w-full overflow-x-auto">
              <table class="w-full min-w-max">
                <thead class="bg-green-700 text-white">
                  <tr>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Ticket Code</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Event</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Date</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Ticket Holder</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Transaction ID</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ticket in validTickets" :key="ticket.id"
                    class="border-b border-creme-200 hover:bg-creme-50">
                    <td class="px-4 py-3 font-mono font-semibold text-gray-900 whitespace-nowrap">{{ ticket.ticket_code
                    }}</td>
                    <td class="px-4 py-3 font-marketing text-gray-900">{{ ticket.event_title }}</td>
                    <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap">{{
                      formatDateTime(ticket.event_date) }}</td>
                    <td class="px-4 py-3 font-marketing text-gray-900">{{ ticket.ticket_holder_name || 'Guest' }}</td>
                    <td class="px-4 py-3 font-mono text-sm text-gray-600 whitespace-nowrap">{{ ticket.transaction_id }}
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap">
                      <button @click="markTicketUsed(ticket.id)"
                        class="px-4 py-1 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing text-sm">
                        Mark as Used
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="validTicketsPagination && validTicketsPagination.total_pages > 1"
              class="p-4 flex justify-between items-center border-t border-creme-200">
              <button @click="loadValidTickets(validTicketsPage - 1)" :disabled="!validTicketsPagination.prev_page"
                :class="['px-4 py-2 rounded-md font-marketing', validTicketsPagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                Previous
              </button>
              <span class="font-marketing text-gray-700">
                Page {{ validTicketsPagination.page }} of {{ validTicketsPagination.total_pages }} ({{
                  validTicketsPagination.total }} total)
              </span>
              <button @click="loadValidTickets(validTicketsPage + 1)" :disabled="!validTicketsPagination.next_page"
                :class="['px-4 py-2 rounded-md font-marketing', validTicketsPagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                Next
              </button>
            </div>
          </div>
        </div>

        <div v-if="activeTicketTab === 'used'">
          <div v-if="usedTicketsLoading"
            class="bg-white p-8 rounded-lg shadow-md text-center text-gray-500 font-marketing">
            Loading used tickets...
          </div>
          <div v-else-if="usedTickets.length === 0"
            class="bg-white p-8 rounded-lg shadow-md text-center text-gray-500 font-marketing">
            No used tickets found
          </div>
          <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="p-4 bg-green-50 border-b border-green-200">
              <p class="font-marketing font-semibold text-primary">
                Used Tickets ({{ usedTicketsPagination?.total || 0 }} total)
              </p>
            </div>
            <div class="w-full overflow-x-auto">
              <table class="w-full min-w-max">
                <thead class="bg-green-700 text-white">
                  <tr>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Ticket Code</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Event</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Date</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Ticket Holder</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Transaction ID</th>
                    <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Used At</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ticket in usedTickets" :key="ticket.id"
                    class="border-b border-creme-200 hover:bg-creme-50">
                    <td class="px-4 py-3 font-mono font-semibold text-gray-900 whitespace-nowrap">{{ ticket.ticket_code
                    }}</td>
                    <td class="px-4 py-3 font-marketing text-gray-900">{{ ticket.event_title }}</td>
                    <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap">{{
                      formatDateTime(ticket.event_date) }}</td>
                    <td class="px-4 py-3 font-marketing text-gray-900">{{ ticket.ticket_holder_name || 'Guest' }}</td>
                    <td class="px-4 py-3 font-mono text-sm text-gray-600 whitespace-nowrap">{{ ticket.transaction_id }}
                    </td>
                    <td class="px-4 py-3 font-marketing text-gray-600 whitespace-nowrap">
                      {{ ticket.used_at ? formatDateTime(ticket.used_at) : 'N/A' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="usedTicketsPagination && usedTicketsPagination.total_pages > 1"
              class="p-4 flex justify-between items-center border-t border-creme-200">
              <button @click="loadUsedTickets(usedTicketsPage - 1)" :disabled="!usedTicketsPagination.prev_page"
                :class="['px-4 py-2 rounded-md font-marketing', usedTicketsPagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                Previous
              </button>
              <span class="font-marketing text-gray-700">
                Page {{ usedTicketsPagination.page }} of {{ usedTicketsPagination.total_pages }} ({{
                  usedTicketsPagination.total }} total)
              </span>
              <button @click="loadUsedTickets(usedTicketsPage + 1)" :disabled="!usedTicketsPagination.next_page"
                :class="['px-4 py-2 rounded-md font-marketing', usedTicketsPagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BaseModal v-model="showCropModal" content-class="max-w-4xl max-h-[90vh]" @close="closeCropModal">
      <div class="mb-4">
        <h3 class="text-xl font-marketing font-bold text-primary">Crop Event Image</h3>
      </div>
      <div v-if="loadingCropImage" class="mb-4 flex items-center justify-center" style="height: 700px;">
        <Loader size="large" />
      </div>
      <div v-else-if="cropImageSrc" class="mb-4 relative" style="height: 700px; width: 100%;">
        <Cropper :key="`cropper-${cropModalKey}-${cropImageSrc}`" ref="cropper" :src="cropImageSrc"
          :default-boundaries="'fill'" :stencil-props="{
            aspectRatio: null
          }" @change="onCropChange" @ready="onCropperReady" style="width: 100%; height: 100%;" />
      </div>
      <div v-else class="mb-4 flex items-center justify-center" style="height: 700px;">
        <p class="text-gray-500 font-sans">No image selected</p>
      </div>

      <div v-if="cropImageSrc && canZoomOut" class="mb-4 flex justify-center">
        <button @click="fitImageToCrop"
          class="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-marketing font-semibold text-sm shadow-sm flex items-center gap-2"
          title="Fit image to crop area (zoom out)">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
          </svg>
          Fit to Crop
        </button>
      </div>

      <div v-if="cropResult && cropResult.coordinates" class="mb-6 p-4 bg-creme-100 rounded-lg border border-creme-300">
        <h4 class="text-sm font-marketing font-semibold text-primary mb-3">Preview: How it will appear alongside other
          events</h4>
        <div class="flex items-end gap-4 justify-center flex-wrap">
          <div class="flex flex-col items-center">
            <div :style="getPreviewEventStyle(cropResult.coordinates)"
              class="bg-white border-2 border-gray-300 shadow-md mb-2 overflow-hidden">
              <img v-if="previewImageUrl" :src="previewImageUrl" alt="Preview" class="w-full h-full object-cover" />
            </div>
            <p class="text-xs text-gray-600 font-sans">Current Event</p>
            <p class="text-xs text-gray-500 font-sans mt-1">{{ Math.round(cropResult.coordinates.width) }} × {{
              Math.round(cropResult.coordinates.height) }}px</p>
          </div>
          <div class="flex flex-col items-center">
            <div :style="getReferenceEventStyle()"
              class="bg-white border-2 border-gray-300 shadow-md mb-2 overflow-hidden">
              <div class="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-600 font-sans">Reference Event</p>
            <p class="text-xs text-gray-500 font-sans mt-1">256 × 192px</p>
          </div>
        </div>
        <div v-if="cropResult.coordinates.width > 400"
          class="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 font-sans">
          ⚠️ Width exceeds maximum threshold (400px). Event image will be scaled down to fit.
        </div>
      </div>

      <div class="flex gap-2 justify-end">
        <button @click="closeCropModal"
          class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-marketing">
          Cancel
        </button>
        <button @click="applyCrop"
          class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
          Apply Crop
        </button>
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { adminService } from '../../services/admin'
import { API_BASE_URL } from '../../config'
import PageDescriptionEditor from './PageDescriptionEditor.vue'
import Loader from '../Loader.vue'
import BaseModal from './BaseModal.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import { formatDateTime } from '../../utils/date'
import { useScrollToTop } from '../../composables/useScrollToTop'

/**
 * CRITICAL: Unified Ticket Structure
 * 
 * All tickets in this component use the standardized TicketInfo structure
 * defined in /website/src/types/ticket.js which matches the backend
 * TicketInfo struct in /server/src/models.rs
 * 
 * When marking tickets as used:
 * - The backend atomically updates BOTH the tickets table AND user_event_purchases table
 * - Both tables use the same ticket_code to maintain synchronization
 * - This prevents issues where tickets are marked used in one table but not the other
 * 
 * Expected ticket fields from API:
 * - id: number
 * - user_id: number | null
 * - event_id: number
 * - event_title: string
 * - event_date: string (EST formatted)
 * - event_location: string | null
 * - ticket_code: string (unique identifier - SAME in both tables)
 * - ticket_holder_name: string
 * - sale_id: number
 * - transaction_id: string
 * - quantity: number (always 1, each ticket is separate)
 * - ticket_price: string
 * - total_price: string
 * - is_used: boolean (synchronized across both tables)
 * - used_at: string | null (EST formatted, from tickets table)
 * - purchase_date: string (EST formatted)
 * - created_at: string (EST formatted)
 */

const { scrollToTop, watchAndScroll } = useScrollToTop()

const events = ref([])
const showForm = ref(false)

// Watch showForm and scroll to top when it becomes true
watchAndScroll(showForm)

const editingEvent = ref(null)
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)
const { loading, setLoading } = useLoadingState(false)
const fileInput = ref(null)
const imagePreview = ref('')
const uploading = ref(false)
const showCropModal = ref(false)
const cropper = ref(null)
const cropImageSrc = ref('')
const cropResult = ref(null)
const selectedFile = ref(null)
const previewImageUrl = ref('')
const canZoomOut = ref(false)
const imageNaturalSize = ref({ width: 0, height: 0 })
const loadingCropImage = ref(false)
const cropModalKey = ref(0)

const tickets = ref([])
const ticketsLoading = ref(false)
const transactionSearch = ref('')
const validTickets = ref([])
const usedTickets = ref([])
const validTicketsPage = ref(1)
const usedTicketsPage = ref(1)
const validTicketsPagination = ref(null)
const usedTicketsPagination = ref(null)
const validTicketsLoading = ref(false)
const usedTicketsLoading = ref(false)
const activeTicketTab = ref('valid')

const formData = ref({
  title: '',
  description: '',
  date: '',
  location: '',
  image: '',
  image_crop_dimensions: null,
  ticket_price: null,
  max_attendees: null
})

const loadEvents = async (page = 1) => {
  setLoading(true)
  try {
    const response = await adminService.getEvents(page, limit.value)
    if (response.data && response.pagination) {
      events.value = response.data
      pagination.value = response.pagination
      currentPage.value = page
    } else {
      events.value = Array.isArray(response) ? response : []
      pagination.value = null
    }
  } catch (error) {
    console.error('Failed to load events:', error)
    events.value = []
    pagination.value = null
  } finally {
    setLoading(false)
  }
}

const editEvent = async (event) => {
  try {
    const fullEvent = await adminService.getEvent(event.id)
    editingEvent.value = fullEvent
    const eventDate = new Date(fullEvent.date)
    const localDate = new Date(eventDate.getTime() - eventDate.getTimezoneOffset() * 60000)
    formData.value = {
      title: fullEvent.title,
      description: fullEvent.description,
      date: localDate.toISOString().slice(0, 16),
      location: fullEvent.location || '',
      image: fullEvent.image || '',
      image_crop_dimensions: fullEvent.image_crop_dimensions || null,
      ticket_price: fullEvent.ticket_price || null,
      max_attendees: fullEvent.max_attendees || null
    }
    imagePreview.value = fullEvent.image || ''
    showForm.value = true
  } catch (error) {
    console.error('Failed to load event:', error)
    alert('Failed to load event details')
  }
}

const cancelForm = () => {
  showForm.value = false
  editingEvent.value = null
  imagePreview.value = ''
  uploading.value = false
  formData.value = {
    title: '',
    description: '',
    date: '',
    location: '',
    image: '',
    image_crop_dimensions: null,
    ticket_price: null,
    max_attendees: null
  }
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  scrollToTop()
}

const handleFileSelect = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  if (showCropModal.value || loadingCropImage.value) {
    return
  }

  const isImage = file.type.startsWith('image/') ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif')
  if (!isImage) {
    alert('Please select an image file')
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    alert('Image size must be less than 10MB')
    return
  }

  let blobUrl
  try {
    const { convertHeicForUpload, convertHeicForPreview } = await import('../../utils/heicConverter')
    const convertedFile = await convertHeicForUpload(file)
    selectedFile.value = convertedFile
    blobUrl = await convertHeicForPreview(file)
  } catch (error) {
    console.error('Failed to convert HEIC:', error)
    selectedFile.value = file
    blobUrl = URL.createObjectURL(file)
  }

  imagePreview.value = blobUrl
  formData.value.image = blobUrl
}

const loadImageForCropping = async () => {
  if (showCropModal.value || loadingCropImage.value) {
    return
  }

  const imageUrl = imagePreview.value || formData.value.image
  if (!imageUrl) {
    alert('Please upload an image or enter an image URL first')
    return
  }

  loadingCropImage.value = true
  cropResult.value = null
  previewImageUrl.value = ''
  canZoomOut.value = false
  imageNaturalSize.value = { width: 0, height: 0 }

  let blobUrl = imagePreview.value

  try {
    if (!blobUrl || !blobUrl.startsWith('blob:')) {
      let blob
      if (imageUrl.includes('storage.googleapis.com')) {
        const proxyUrl = `${API_BASE_URL}/api/admin/proxy/image?url=${encodeURIComponent(imageUrl)}`
        const response = await fetch(proxyUrl, {
          credentials: 'include'
        })
        if (!response.ok) {
          throw new Error('Failed to load image')
        }
        blob = await response.blob()
      } else {
        const response = await fetch(imageUrl)
        if (!response.ok) {
          throw new Error('Failed to load image')
        }
        blob = await response.blob()
      }

      blobUrl = URL.createObjectURL(blob)
      imagePreview.value = blobUrl
    }

    cropImageSrc.value = blobUrl
    selectedFile.value = null
    cropModalKey.value++
    showCropModal.value = true
    await nextTick()
  } catch (error) {
    console.error('Failed to load image for cropping:', error)
    alert('Failed to load image. Please check the image URL or try uploading the image again.')
  } finally {
    loadingCropImage.value = false
  }
}

const closeCropModal = () => {
  const fileToUpload = selectedFile.value

  if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
    URL.revokeObjectURL(cropImageSrc.value)
  }
  cropImageSrc.value = ''
  cropResult.value = null
  previewImageUrl.value = ''
  showCropModal.value = false
  canZoomOut.value = false
  imageNaturalSize.value = { width: 0, height: 0 }
  loadingCropImage.value = false
  cropper.value = null

  if (fileToUpload && !formData.value.image) {
    imagePreview.value = ''
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  } else if (!formData.value.image) {
    imagePreview.value = ''
  }

  selectedFile.value = null
}

const onCropperReady = () => {
  if (cropper.value) {
    const result = cropper.value.getResult()
    if (result?.imageSize) {
      imageNaturalSize.value = {
        width: result.imageSize.width,
        height: result.imageSize.height
      }
    }
    if (result) {
      cropResult.value = result
      updatePreview()
    }
  }
}

const onCropChange = ({ coordinates, canvas, visibleArea }) => {
  cropResult.value = { coordinates, canvas, visibleArea }
  if (canvas) {
    previewImageUrl.value = canvas.toDataURL('image/jpeg', 0.95)
  }

  if (cropper.value && coordinates) {
    const cropWidth = coordinates.width
    const cropHeight = coordinates.height

    if (cropWidth && cropHeight) {
      const imageWidth = imageNaturalSize.value.width || visibleArea?.width || 0
      const imageHeight = imageNaturalSize.value.height || visibleArea?.height || 0

      if (imageWidth && imageHeight) {
        const needsZoomOut = imageWidth > cropWidth || imageHeight > cropHeight
        canZoomOut.value = needsZoomOut
      }
    }
  }

  updatePreview()
}

const updatePreview = async () => {
  if (!cropper.value || !cropResult.value) return

  try {
    const canvas = cropResult.value.canvas
    if (canvas) {
      previewImageUrl.value = canvas.toDataURL()
    }
  } catch (error) {
    console.error('Failed to update preview:', error)
  }
}

const applyCrop = async () => {
  if (!cropper.value || !cropResult.value) {
    alert('Please select a crop area by adjusting the crop box')
    return
  }

  try {
    const { coordinates, canvas } = cropResult.value

    if (!coordinates || !coordinates.width || !coordinates.height) {
      alert('Please select a crop area')
      return
    }

    formData.value.image_crop_dimensions = {
      width: coordinates.width,
      height: coordinates.height
    }

    if (canvas) {
      uploading.value = true

      canvas.toBlob(async (blob) => {
        if (!blob) {
          alert('Failed to create cropped image')
          uploading.value = false
          return
        }

        try {
          const currentImageUrl = formData.value.image || imagePreview.value
          let fileName = selectedFile.value?.name || (currentImageUrl ? currentImageUrl.split('/').pop().split('?')[0] : 'cropped-image.jpg')
          const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '')
          fileName = `${nameWithoutExt}.jpg`

          const formDataUpload = new FormData()
          formDataUpload.append('file', blob, fileName)

          const uploadedUrl = await adminService.uploadEventImage(formDataUpload)
          if (uploadedUrl) {
            formData.value.image = uploadedUrl
            imagePreview.value = uploadedUrl

            if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
              URL.revokeObjectURL(cropImageSrc.value)
            }

            uploading.value = false
            showCropModal.value = false
            selectedFile.value = null
            cropResult.value = null
          } else {
            throw new Error('No URL returned from server')
          }
        } catch (error) {
          console.error('Failed to upload cropped image:', error)
          const errorMessage = error.response?.data?.error || error.message || 'Failed to upload cropped image. Please try again.'
          alert(errorMessage)
          uploading.value = false
        }
      }, 'image/jpeg', 0.95)
    } else {
      alert('Failed to get cropped canvas')
    }
  } catch (error) {
    console.error('Failed to apply crop:', error)
    alert('Failed to apply crop. Please try again.')
  }
}

const getImageStyle = () => {
  if (formData.value.image_crop_dimensions &&
    formData.value.image_crop_dimensions.width > 0 &&
    formData.value.image_crop_dimensions.height > 0) {
    const { width, height } = formData.value.image_crop_dimensions

    const maxWidth = 400
    let displayWidth = width
    let displayHeight = height

    if (width > maxWidth) {
      const scale = maxWidth / width
      displayWidth = maxWidth
      displayHeight = height * scale
    }

    return {
      width: `${displayWidth}px`,
      height: `${displayHeight}px`,
      objectFit: 'cover'
    }
  }
  return {
    maxWidth: '400px',
    height: '192px',
    objectFit: 'cover'
  }
}

const getPreviewEventStyle = (coordinates) => {
  if (!coordinates || !coordinates.width || !coordinates.height) {
    return { width: '256px', height: '192px' }
  }

  let displayWidth = coordinates.width
  let displayHeight = coordinates.height

  const MAX_WIDTH = 400
  if (coordinates.width > MAX_WIDTH) {
    const scale = MAX_WIDTH / coordinates.width
    displayWidth = MAX_WIDTH
    displayHeight = coordinates.height * scale
  }

  return {
    width: `${displayWidth}px`,
    height: `${displayHeight}px`
  }
}

const getReferenceEventStyle = () => {
  return {
    width: '256px',
    height: '192px'
  }
}

const getTrueToScalePreviewStyle = () => {
  if (formData.value.image_crop_dimensions && formData.value.image_crop_dimensions.width > 0 && formData.value.image_crop_dimensions.height > 0) {
    const { width, height } = formData.value.image_crop_dimensions
    const maxPreviewWidth = 400
    let displayWidth = width
    let displayHeight = height

    if (width > maxPreviewWidth) {
      const scale = maxPreviewWidth / width
      displayWidth = maxPreviewWidth
      displayHeight = height * scale
    }

    return {
      width: `${displayWidth}px`,
      height: `${displayHeight}px`
    }
  }
  return {
    width: '256px',
    height: '192px'
  }
}

const updatePreviewDimensions = () => {
  if (!formData.value.image_crop_dimensions) {
    formData.value.image_crop_dimensions = { width: 0, height: 0 }
  }
}

const Math = window.Math

const fitImageToCrop = async () => {
  if (!cropper.value || !cropResult.value?.coordinates) {
    return
  }

  try {
    const coordinates = cropResult.value.coordinates
    const cropWidth = coordinates.width
    const cropHeight = coordinates.height

    if (!cropWidth || !cropHeight) {
      return
    }

    const imageWidth = imageNaturalSize.value.width
    const imageHeight = imageNaturalSize.value.height

    if (!imageWidth || !imageHeight) {
      const result = cropper.value.getResult()
      if (result?.imageSize) {
        imageNaturalSize.value = {
          width: result.imageSize.width,
          height: result.imageSize.height
        }
      } else {
        return
      }
    }

    const finalImageWidth = imageNaturalSize.value.width
    const finalImageHeight = imageNaturalSize.value.height

    if (!finalImageWidth || !finalImageHeight) {
      return
    }

    const scaleX = cropWidth / finalImageWidth
    const scaleY = cropHeight / finalImageHeight
    const scale = Math.min(scaleX, scaleY)

    if (scale < 1 && cropper.value.zoom) {
      const result = cropper.value.getResult()
      const currentZoom = result?.transforms?.zoom || 1
      const newZoom = Math.max(0.1, Math.min(10, currentZoom * scale))

      cropper.value.zoom(newZoom)

      await nextTick()

      if (cropper.value.move) {
        const updatedResult = cropper.value.getResult()
        const visibleArea = updatedResult?.visibleArea || {}
        const centerX = visibleArea.left + (visibleArea.width / 2)
        const centerY = visibleArea.top + (visibleArea.height / 2)

        cropper.value.move({
          left: centerX - (cropWidth / 2),
          top: centerY - (cropHeight / 2)
        })
      }
    }
  } catch (error) {
    console.error('Error fitting image to crop:', error)
  }
}

const clearImage = () => {
  formData.value.image = ''
  formData.value.image_crop_dimensions = null
  imagePreview.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const saveEvent = async () => {
  try {
    // First, upload the blob image if there's a selected file that hasn't been uploaded yet
    if (selectedFile.value && imagePreview.value && imagePreview.value.startsWith('blob:')) {
      uploading.value = true
      try {
        const formDataUpload = new FormData()
        formDataUpload.append('file', selectedFile.value, selectedFile.value.name)
        const uploadedUrl = await adminService.uploadEventImage(formDataUpload)
        formData.value.image = uploadedUrl
        imagePreview.value = uploadedUrl
        selectedFile.value = null
      } catch (error) {
        console.error('Failed to upload image:', error)
        alert('Failed to upload image. Please try again.')
        uploading.value = false
        return
      } finally {
        uploading.value = false
      }
    }

    const eventData = {
      ...formData.value,
      date: new Date(formData.value.date).toISOString(),
      image_crop_dimensions: formData.value.image_crop_dimensions && (formData.value.image_crop_dimensions.width > 0 || formData.value.image_crop_dimensions.height > 0)
        ? formData.value.image_crop_dimensions
        : null
    }

    if (eventData.image && eventData.image_crop_dimensions &&
      eventData.image_crop_dimensions.width > 0 && eventData.image_crop_dimensions.height > 0) {
      try {
        const { resizeImage } = await import('../../utils/imageResize.js')
        const resizedBlob = await resizeImage(
          eventData.image,
          eventData.image_crop_dimensions.width,
          eventData.image_crop_dimensions.height
        )
        const formDataUpload = new FormData()
        formDataUpload.append('file', resizedBlob, 'resized-image.jpg')
        const resizedUrl = await adminService.uploadEventImage(formDataUpload)
        eventData.image = resizedUrl
      } catch (error) {
        console.error('Failed to resize image:', error)
      }
    }

    if (editingEvent.value) {
      await adminService.updateEvent(editingEvent.value.id, eventData)
    } else {
      await adminService.createEvent(eventData)
    }
    await loadEvents(currentPage.value)
    cancelForm()
  } catch (error) {
    console.error('Failed to save event:', error)
    alert('Failed to save event. Please try again.')
  }
}

const deleteEvent = async (id) => {
  if (!confirm('Are you sure you want to delete this event?')) return

  try {
    await adminService.deleteEvent(id)
    await loadEvents(currentPage.value)
  } catch (error) {
    console.error('Failed to delete event:', error)
    alert('Failed to delete event. Please try again.')
  }
}

const loadValidTickets = async (page = 1) => {
  validTicketsLoading.value = true
  validTicketsPage.value = page
  try {
    const response = await adminService.getValidTickets(page, 10)
    if (response.data && Array.isArray(response.data)) {
      validTickets.value = response.data
      validTicketsPagination.value = response.pagination
    } else {
      validTickets.value = []
      validTicketsPagination.value = null
    }
  } catch (error) {
    console.error('Failed to load valid tickets:', error)
    alert('Failed to load valid tickets. Please try again.')
    validTickets.value = []
    validTicketsPagination.value = null
  } finally {
    validTicketsLoading.value = false
  }
}

const loadUsedTickets = async (page = 1) => {
  usedTicketsLoading.value = true
  usedTicketsPage.value = page
  try {
    const response = await adminService.getUsedTickets(page, 10)
    if (response.data && Array.isArray(response.data)) {
      usedTickets.value = response.data
      usedTicketsPagination.value = response.pagination
    } else {
      usedTickets.value = []
      usedTicketsPagination.value = null
    }
  } catch (error) {
    console.error('Failed to load used tickets:', error)
    alert('Failed to load used tickets. Please try again.')
    usedTickets.value = []
    usedTicketsPagination.value = null
  } finally {
    usedTicketsLoading.value = false
  }
}

const searchTickets = async () => {
  const searchTerm = transactionSearch.value.trim()
  if (!searchTerm) {
    alert('Please enter a ticket code or transaction ID')
    return
  }

  ticketsLoading.value = true
  try {
    const response = await adminService.getTickets(1, 10, null, searchTerm, searchTerm)
    if (response.data && Array.isArray(response.data)) {
      tickets.value = response.data
    } else {
      tickets.value = []
    }
  } catch (error) {
    console.error('Failed to search tickets:', error)
    alert('Failed to search tickets. Please try again.')
    tickets.value = []
  } finally {
    ticketsLoading.value = false
  }
}

const clearTicketSearch = () => {
  transactionSearch.value = ''
  tickets.value = []
}

const markTicketUsed = async (ticketId) => {
  if (!confirm('Mark this ticket as used?')) return

  try {
    // CRITICAL: This endpoint marks the ticket as used in BOTH:
    // 1. tickets table (used by admin for scanning)
    // 2. user_event_purchases table (used by users to view their tickets)
    // The backend uses a database transaction to ensure atomic updates
    await adminService.markTicketUsed(ticketId)

    // Reload the appropriate ticket list to reflect the change
    if (transactionSearch.value) {
      await searchTickets()
    } else {
      if (activeTicketTab.value === 'valid') {
        await loadValidTickets(validTicketsPage.value)
      } else {
        await loadUsedTickets(usedTicketsPage.value)
      }
    }
  } catch (error) {
    console.error('Failed to mark ticket as used:', error)
    alert('Failed to mark ticket as used. Please try again.')
  }
}

const markAllTicketsUsed = async () => {
  if (!confirm(`Mark all ${tickets.value.length} ticket(s) as used?`)) return

  try {
    // CRITICAL: This endpoint marks ALL tickets for a transaction as used in BOTH:
    // 1. tickets table (used by admin for scanning)
    // 2. user_event_purchases table (used by users to view their tickets)
    // The backend uses a database transaction to ensure atomic updates
    await adminService.markTicketsUsedByTransaction(transactionSearch.value.trim())
    await searchTickets()
    alert('All tickets marked as used successfully')
  } catch (error) {
    console.error('Failed to mark tickets as used:', error)
    alert('Failed to mark tickets as used. Please try again.')
  }
}

const hasUnusedTickets = computed(() => {
  return tickets.value.some(t => !t.is_used)
})

onMounted(() => {
  loadEvents(1)
  loadValidTickets(1)
})
</script>
